import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CategoryMapping } from '../../../core/models/category-mapping';
import { CategoryMappingService } from '../../../core/services/category-mapping.service';
import { CategoryFormComponent } from './category-form/category-form.component';
import { CategoryMappingID } from '../../../core/models/category-mapping-id';
import { CategoryMappingsOptions } from '../../../core/models/category-mappings-options';
import { Subject, takeUntil } from 'rxjs';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { CountryService } from '../../../core/services/country.service';
import { CareerPathService } from '../../../core/services/career-path.service';
import { CareerPath } from '../../../core/models/career-path';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-category',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    MultiSelectModule,
    FormsModule,
    DropdownModule,
    InputTextModule,
    TagModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './category.component.html',
  styleUrl: './category.component.scss',
  providers: [ConfirmationService],
})
export class CategoryComponent implements OnDestroy, OnInit {
  categories: CategoryMapping[] = [];
  filteredCategories: CategoryMapping[] = [];
  ref: DynamicDialogRef | undefined;
  private destroy$ = new Subject<void>();
  categoryMappingsOptions: CategoryMappingsOptions = {
    divisions: [],
    professionalCategories: [],
    businessUnits: [],
    talentFamilies: [],
    talentSegments: [],
    jobRoles: [],
  };
  Permission = Permission;
  translations: any = {};
  // Columns
  selectedColumn: any[] = [];
  showFilterLabels: boolean = false;
  cols: any[] = [];

  filters: any = {
    'categoryMappingsId.level': null,
    'categoryMappingsId.careerPathId': null,
    'categoryMappingsId.categoryShortLabel': null,
    'categoryMappingsId.division': null,
    'categoryMappingsId.practice': null,
    categoryLongLabel: null,
    group1: null,
    group2: null,
    geoCountry: null,
    professionalCategorization: null,
    businessUnit: null,
    talentFamily: null,
    elTalentSegment: null,
    jobRole: null,
    supervisor: null,
    archived: false,
  };
  archivedOptions: any[] = [];
  selectedArchiveValue: boolean = false;

  careerPaths: CareerPath[] = [];
  countries: string[] = [];
  levels: number[] = [];
  practices: string[] = [];

  constructor(
    private countryService: CountryService,
    private careerPathService: CareerPathService,
    private categoryMappingService: CategoryMappingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.category.title',
        'layout.configurations.category.filter',
        'layout.configurations.category.clear',
        'layout.configurations.category.chooseColums',
        'layout.configurations.category.itemsSelected',
        'layout.configurations.category.level',
        'layout.configurations.category.careerPath',
        'layout.configurations.category.shortLabel',
        'layout.configurations.category.longLabel',
        'layout.configurations.category.practice',
        'layout.configurations.category.group1',
        'layout.configurations.category.group2',
        'layout.configurations.category.geoCountry',
        'layout.configurations.category.professionalCategorization',
        'layout.configurations.category.businessUnit',
        'layout.configurations.category.division',
        'layout.configurations.category.talentFamily',
        'layout.configurations.category.elTalentSegment',
        'layout.configurations.category.jobRole',
        'layout.configurations.category.isSupervisor',
        'layout.configurations.category.archived',
        'layout.configurations.category.actions',
        'layout.configurations.category.addCategory.title',
        'layout.configurations.category.addCategory.successMessage.title',
        'layout.configurations.category.addCategory.successMessage.detail',
        'layout.configurations.category.editCategory.title',
        'layout.configurations.category.editCategory.successMessage.title',
        'layout.configurations.category.editCategory.successMessage.detail',
        'layout.configurations.category.deleteCategory.title',
        'layout.configurations.category.deleteCategory.detail',
        'layout.configurations.category.deleteCategory.yes',
        'layout.configurations.category.deleteCategory.no',
        'layout.configurations.category.deleteCategory.successMessage.title',
        'layout.configurations.category.deleteCategory.successMessage.detail',
        'layout.configurations.category.deleteCategory.errorMessage.title',
        'layout.configurations.category.deleteCategory.errorMessage.detail',
        'layout.configurations.category.deleteCategory.infoMessage.title',
        'layout.configurations.category.deleteCategory.infoMessage.detail',
        'layout.configurations.category.yes',
        'layout.configurations.category.no'
      ])
      .subscribe((translations) => {
        this.translations = translations;
        this.archivedOptions = [
          { label: this.translations['layout.configurations.category.yes'], value: true, severity: 'success' },
          { label: this.translations['layout.configurations.category.no'], value: false, severity: 'danger' },
        ];
        this.cols = [
          {
            field: 'group1',
            header: this.translations['layout.configurations.category.group1'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'group2',
            header: this.translations['layout.configurations.category.group2'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'geoCountry',
            header: this.translations['layout.configurations.category.geoCountry'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'professionalCategorization',
            header: this.translations['layout.configurations.category.professionalCategorization'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'businessUnit',
            header: this.translations['layout.configurations.category.businessUnit'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'talentFamily',
            header: this.translations['layout.configurations.category.talentFamily'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'elTalentSegment',
            header: this.translations['layout.configurations.category.elTalentSegment'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'jobRole',
            header: this.translations['layout.configurations.category.jobRole'],
            filter: true,
            filterType: 'text',
          },
          {
            field: 'supervisor',
            header: this.translations['layout.configurations.category.isSupervisor'],
            filter: true,
            filterType: 'boolean',
          },
        ];
      });
  }

  getTranslatedValue(archived: boolean): string {
    return archived ? this.translations['layout.configurations.category.yes'] : this.translations['layout.configurations.category.no'];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.getAllCountries();
    this.getAllCareerPaths();
    this.loadSelectedColumn();
    this.loadTranslations();
    this.getAllCategories();
  }

  getAllCategories() {
    this.categoryMappingService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.categories = data.map(cat => ({
          ...cat,
          'careerPath.name': cat.careerPath?.name,
          'careerPath.practice': cat.careerPath?.practice,
          'categoryMappingsId.categoryShortLabel': cat.categoryMappingsId?.categoryShortLabel,
          'categoryMappingsId.level': cat.categoryMappingsId?.level,
          'categoryMappingsId.division': cat.categoryMappingsId?.division
          }));
        
        this.sortCategories();
        this.filteredCategories = [...this.categories];
        this.filterAndMapFormOptions(data);
        this.onFilter();
      });
  }

  sortCategories() {
    this.categories.sort((a, b) => {
      const hasModifiedDateA = !!a.modifiedDate;
      const hasModifiedDateB = !!b.modifiedDate;

      if (hasModifiedDateA && !hasModifiedDateB) {
        return -1;
      }
      if (!hasModifiedDateA && hasModifiedDateB) {
        return 1;
      }
      if (b.modifiedDate && a.modifiedDate && hasModifiedDateA && hasModifiedDateB) {
        return new Date(b.modifiedDate).getTime() - new Date(a.modifiedDate).getTime();
      }
      return 0;
    });
  }

  filterAndMapFormOptions(data: CategoryMapping[]) {
    this.categoryMappingsOptions.divisions = [
      ...new Set(
        data
          .filter((category) => category.categoryMappingsId)
          .map((cat) => cat.categoryMappingsId)
          .map((catId) => catId.division),
      ),
    ];
    this.categoryMappingsOptions.professionalCategories = [
      ...new Set(
        data.filter((category) => category.professionalCategorization).map((cat) => cat.professionalCategorization),
      ),
    ];
    this.categoryMappingsOptions.businessUnits = [
      ...new Set(data.filter((category) => category.businessUnit).map((cat) => cat.businessUnit)),
    ];
    this.categoryMappingsOptions.talentFamilies = [
      ...new Set(data.filter((category) => category.talentFamily).map((cat) => cat.talentFamily)),
    ];
    this.categoryMappingsOptions.talentSegments = [
      ...new Set(data.filter((category) => category.elTalentSegment).map((cat) => cat.elTalentSegment)),
    ];
    this.categoryMappingsOptions.jobRoles = [
      ...new Set(data.filter((category) => category.jobRole).map((cat) => cat.jobRole)),
    ];
  }

  onAdd() {
    this.ref = this.dialogService.open(CategoryFormComponent, {
      header: this.translations['layout.configurations.category.addCategory.title'],
      width: '640px',
      contentStyle: { overflow: 'auto' },
      data: {
        formOptions: this.categoryMappingsOptions,
        editMode: false,
      },
    });

    this.ref.onClose.subscribe((category) => {
      if (category) {
        this.getAllCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.category.addCategory.successMessage.title'],
          detail: this.translations['layout.configurations.category.addCategory.successMessage.detail'],
        });
      }
    });
  }

  onEdit(category: CategoryMapping) {
    this.ref = this.dialogService.open(CategoryFormComponent, {
      header: this.translations['layout.configurations.category.editCategory.title'],
      width: '640px',
      contentStyle: { overflow: 'auto' },
      data: {
        category: category,
        formOptions: this.categoryMappingsOptions,
        editMode: true,
      },
    });
     this.ref.onClose.subscribe((result) => {
    if (!result) return;

    if (result.type === 'no-change') {
      this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
      return;
    }
      if (result.type === 'updated') {
        this.getAllCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.category.editCategory.successMessage.title'],
          detail: this.translations['layout.configurations.category.editCategory.successMessage.detail'],
        });
      }
    });
  }

  onDelete(event: Event, categoryId: CategoryMappingID) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.category.deleteCategory.detail'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.category.deleteCategory.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.category.deleteCategory.yes'],
      rejectLabel: this.translations['layout.configurations.category.deleteCategory.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteCategory(categoryId);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.category.deleteCategory.infoMessage.title'],
          detail: this.translations['layout.configurations.category.deleteCategory.infoMessage.detail'],
          life: 3000,
        });
      },
    });
  }

  deleteCategory(categoryId: CategoryMappingID) {
    this.categoryMappingService.deleteCategoryMapping(categoryId).subscribe({
      next: () => {
        this.getAllCategories();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.category.deleteCategory.successMessage.title'],
          detail: this.translations['layout.configurations.category.deleteCategory.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting category: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.category.deleteCategory.errorMessage.title'],
          detail: this.translations['layout.configurations.category.deleteCategory.errorMessage.detail'],
        });
      },
    });
  }

  saveSelectedColumn() {
    sessionStorage.setItem('categoriesCols', JSON.stringify(this.selectedColumn));
  }

  loadSelectedColumn(): void {
    const savedColumn = sessionStorage.getItem('categoriesCols');
    if (savedColumn) {
      this.selectedColumn = JSON.parse(savedColumn);
    }
  }

  clearFilters(field?: string) {
    this.filteredCategories = this.categories;
    if (field) {
      this.filters[field] = null;
    } else {
      Object.keys(this.filters).forEach((key) => (this.filters[key] = null));
    }
    this.onFilter();
  }
  toggleFilterLabels() {
    this.showFilterLabels = !this.showFilterLabels;
  }

  onFilter() {
    this.filteredCategories = this.filterCategoryMappings(this.categories, this.filters);
  }

  getAllCountries() {
    this.countryService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries) => (this.countries = countries.map((country) => country.name)));
  }

  getAllCareerPaths() {
    this.careerPathService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.careerPaths = data;
        // Extract unique practices from career paths immediately
        this.practices = [
          ...new Set(
            this.careerPaths
              .filter((careerPath) => careerPath.practice)
              .map((careerPath) => careerPath.practice),
          ),
        ];
        this.getMaxRangeOfLevels(this.careerPaths);
      });
  }

  getMaxRangeOfLevels(careerPaths: CareerPath[]) {
    const maxValue = careerPaths.reduce(
      (prev, current) => (current.numberOfLevels > prev.numberOfLevels ? current : prev),
      careerPaths[0],
    );
    this.levels = [...Array.from({ length: maxValue.numberOfLevels }, (_, i) => i + 1)];
  }

  getCategoryField<CategoryMapping>(
    obj: CategoryMapping,
    fieldName: keyof CategoryMapping,
  ): CategoryMapping[keyof CategoryMapping] | null {
    const fields = fieldName.toString().split('.');
    let currentValue: any = obj;
    for (const field of fields) {
      if (currentValue && field in currentValue) {
        currentValue = currentValue[field];
      } else {
        return null;
      }
    }
    return currentValue;
  }

  getValue<T extends object>(obj: T, path: string): any {
    return path.split('.').reduce((acc, part) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return (acc as Record<string, any>)[part];
      }
      return undefined;
    }, obj);
  }

  filterCategoryMappings(categoryMappings: CategoryMapping[], filters: any): CategoryMapping[] {
    return categoryMappings.filter((item) => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (filterValue === null || filterValue === undefined || filterValue === '') {
          return true;
        }
        const itemValue = this.getValue(item, key);
        if (key === 'categoryMappingsId.careerPathId' && (filterValue as any)?.id) {
          return itemValue === (filterValue as any).id;
        }
        if (typeof filterValue === 'string' && typeof itemValue === 'string') {
          return itemValue.toLowerCase().includes(filterValue.toLowerCase());
        }
        return itemValue === filterValue;
      });
    });
  }

  setCareerPathFilter(event: any) {
    if (event.value) {
      this.filters['categoryMappingsId.careerPathId'] = event.value;
      this.onFilter();
    }
  }
}
